import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pin } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { PinCard } from './components/PinCard';
import { useMyPins, usePinActions } from './hooks/usePins';

/**
 * Full-page view of everything the user has pinned across their chats — the
 * "See all" destination from the inbox rail. Styled like the Locker (icon chip
 * header + tile grid) but as a full overlay with a back arrow to close out.
 */
export function PinnedMessagesPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: pins = [], isLoading } = useMyPins();
  const { unpin } = usePinActions();

  function back() {
    if (window.history.length > 1) navigate(-1);
    else navigate(ROUTES.inbox);
  }

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-[linear-gradient(180deg,rgba(10,10,14,0.2),rgba(10,10,14,0.34))] backdrop-blur-[22px] backdrop-saturate-[1.3]">
      <button
        type="button"
        onClick={back}
        aria-label="Back"
        className="fixed left-4 top-5 z-20 grid h-9 w-9 place-items-center text-white/70 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] transition-colors hover:text-white sm:left-7"
      >
        <ArrowLeft size={18} />
      </button>

      <div className="mx-auto max-w-[1320px] px-5 pb-16 pt-16 sm:px-8">
        {/* Locker-style header */}
        <div className="mb-8 flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/[0.08] text-accent shadow-[0_10px_30px_rgba(84,185,254,0.18)]">
            <Pin size={20} strokeWidth={2} />
          </span>
          <div>
            <h1 className="text-2xl font-black tracking-[-0.6px] text-white">Pinned messages</h1>
            <p className="text-[13px] font-semibold text-white/50">
              Saved images, links, and messages from across your chats.
            </p>
          </div>
        </div>

        {isLoading || authLoading ? (
          <div className="grid h-[320px] place-items-center">
            <Spinner />
          </div>
        ) : !user ? (
          <EmptyState body="Log in to see the messages you've pinned." />
        ) : pins.length ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {pins.map((pin) => (
              <PinCard
                key={pin.pinId}
                pin={pin}
                onOpen={() => navigate(`/dm/${pin.conversationId}`)}
                onUnpin={() =>
                  unpin.mutate(pin.messageId, { onSuccess: () => toast('Removed from pinned.') })
                }
              />
            ))}
          </div>
        ) : (
          <EmptyState body="Hover any message in a chat and hit the pin to save it here." />
        )}
      </div>
    </div>
  );
}

function EmptyState({ body }: { body: string }) {
  return (
    <div className="mx-auto flex max-w-[460px] flex-col items-center gap-3 rounded-2xl border border-dashed border-white/[0.12] bg-white/[0.025] px-6 py-16 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-full bg-white/[0.055] text-accent">
        <Pin size={24} />
      </span>
      <h2 className="text-xl font-black tracking-[-0.4px] text-white">No pinned messages yet</h2>
      <p className="max-w-[380px] text-sm font-semibold leading-6 text-white/52">{body}</p>
    </div>
  );
}
